/**
 * VideoStreamPlayer Component
 * Handles H.264 video playback via Media Source Extensions (MSE) with jmuxer
 * Falls back to displaying WebP/JPEG frames when H.264 is not available
 */

import { useRef, useEffect, useState, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { useLiveStreamStore } from '../../stores/liveStreamStore';
import clsx from 'clsx';

const STREAM_WIDTH = 854;  // engine medium quality resolution
const STREAM_HEIGHT = 480;

// The H.264 video is buffered by MSE/jmuxer before it plays, so what's on
// screen lags the live capture by roughly this much. Detections arrive with
// near-zero buffering, so we hold each one for the same delay to keep the
// bounding box aligned with a walking person instead of leading them.
const DETECTION_SYNC_DELAY_MS = 400;

/**
 * Returns `value` delayed by `delayMs`. Each new value is held in a short
 * timer so the whole detection stream is shifted to match the video buffer.
 */
const useDelayedValue = (value, delayMs) => {
  const [delayed, setDelayed] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDelayed(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return delayed;
};

// Check if MSE and H.264 are supported
const checkMSESupport = () => {
  if (typeof window === 'undefined') return false;
  if (!window.MediaSource) return false;

  // Check for H.264 codec support
  const mimeTypes = [
    'video/mp4; codecs="avc1.42E01E"', // H.264 Baseline
    'video/mp4; codecs="avc1.4D401E"', // H.264 Main
    'video/mp4; codecs="avc1.64001E"', // H.264 High
  ];

  return mimeTypes.some(mime => MediaSource.isTypeSupported(mime));
};

const MSE_SUPPORTED = checkMSESupport();

/**
 * H.264 Video Player using jmuxer for MSE playback
 */
const H264Player = memo(forwardRef(({ cameraId, fps = 15, onError }, ref) => {
  const videoRef = useRef(null);
  const jmuxerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize jmuxer
  useEffect(() => {
    let JMuxer = null;

    const initJMuxer = async () => {
      try {
        // Dynamic import for jmuxer
        const module = await import('jmuxer');
        JMuxer = module.default;

        if (!videoRef.current) return;

        // Create jmuxer instance
        jmuxerRef.current = new JMuxer({
          node: videoRef.current,
          mode: 'video',
          // Match jmuxer's playback rate to the stream's actual FPS. Hardcoding
          // 15 while the engine sent 10 (medium) made jmuxer expect frames faster
          // than they arrived → buffer starvation and visible stutter.
          fps,
          flushingTime: 100,
          clearBuffer: true,
          onReady: () => {
            console.log(`[H264Player] jmuxer ready for camera ${cameraId}`);
            setIsInitialized(true);
          },
          onError: (error) => {
            console.error(`[H264Player] jmuxer error:`, error);
            onError?.(error);
          },
        });
      } catch (error) {
        console.error('[H264Player] Failed to initialize jmuxer:', error);
        onError?.(error);
      }
    };

    initJMuxer();

    return () => {
      if (jmuxerRef.current) {
        try {
          jmuxerRef.current.destroy();
        } catch (e) {
          // Ignore cleanup errors
        }
        jmuxerRef.current = null;
      }
      setIsInitialized(false);
    };
  }, [cameraId, fps, onError]);

  // Feed H.264 data to jmuxer
  const feedData = useCallback((data, timestamp) => {
    if (!jmuxerRef.current || !isInitialized) return;

    try {
      // Convert base64 to Uint8Array
      const binaryString = atob(data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Feed to jmuxer
      jmuxerRef.current.feed({
        video: bytes,
        duration: 1000 / fps, // Duration in ms based on actual stream FPS
      });
    } catch (error) {
      console.error('[H264Player] Error feeding data:', error);
    }
  }, [isInitialized, fps]);

  // Expose feedH264Data method to parent via ref
  useImperativeHandle(ref, () => ({
    feedH264Data: feedData,
  }), [feedData]);

  return (
    <video
      ref={videoRef}
      className="w-full h-full object-cover"
      autoPlay
      muted
      playsInline
    />
  );
}));

H264Player.displayName = 'H264Player';

/**
 * Image Frame Player for WebP/JPEG fallback
 */
const ImagePlayer = memo(({ frame, className }) => {
  return (
    <img
      src={frame}
      alt="Stream frame"
      className={clsx('w-full h-full object-cover', className)}
      draggable={false}
    />
  );
});

ImagePlayer.displayName = 'ImagePlayer';

/**
 * Stream Stats Overlay
 */
const StreamStats = memo(({ stats, codec, source }) => {
  if (!stats) return null;

  return (
    <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-xs text-white font-mono">
      <div className="flex gap-3">
        {codec && (
          <span className="text-green-400">{codec.toUpperCase()}</span>
        )}
        {stats.fps && (
          <span>{stats.fps} fps</span>
        )}
        {stats.bitrate_kbps && (
          <span>{stats.bitrate_kbps} kbps</span>
        )}
        {stats.compression_ratio && (
          <span className="text-blue-400">
            {((1 - stats.compression_ratio) * 100).toFixed(0)}% saved
          </span>
        )}
        {source === 'edge' && (
          <span className="text-yellow-400">EDGE</span>
        )}
      </div>
    </div>
  );
});

StreamStats.displayName = 'StreamStats';

/**
 * Canvas overlay that draws bounding boxes from stream:detections events
 */
const DetectionOverlay = memo(({ detections }) => {
  const canvasRef = useRef(null);

  // Bbox coords come from the engine in the SOURCE frame's native resolution
  // (e.g. 1920×1080). Size the canvas buffer to match, draw in native coords,
  // and let CSS stretch the whole canvas to fill the container proportionally.
  const bufW = detections?.frameWidth || STREAM_WIDTH;
  const bufH = detections?.frameHeight || STREAM_HEIGHT;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const hasPersons = detections?.persons?.length > 0;
    const hasFires = detections?.fires?.length > 0;
    if (!hasPersons && !hasFires) return;

    ctx.font = 'bold 20px monospace';

    if (hasPersons) {
      detections.persons.forEach(({ bbox, compliant, trackId, violations }) => {
        if (!bbox || bbox.length < 4) return;
        const [x1, y1, x2, y2] = bbox;
        const w = x2 - x1;
        const h = y2 - y1;

        const color = compliant ? '#22c55e' : '#ef4444';
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.strokeRect(x1, y1, w, h);

        const label = compliant ? `#${trackId}` : `#${trackId} ⚠`;
        const textW = ctx.measureText(label).width + 10;
        ctx.fillStyle = compliant ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.9)';
        ctx.fillRect(x1, y1 - 26, textW, 26);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x1 + 5, y1 - 7);

        if (violations?.length) {
          violations.forEach((v, i) => {
            const vLabel = v.replace(/_/g, ' ');
            const vW = ctx.measureText(vLabel).width + 10;
            ctx.fillStyle = 'rgba(239,68,68,0.9)';
            ctx.fillRect(x1, y2 + i * 26, vW, 26);
            ctx.fillStyle = '#fff';
            ctx.fillText(vLabel, x1 + 5, y2 + i * 26 + 19);
          });
        }
      });
    }

    if (hasFires) {
      detections.fires.forEach(({ bbox, className, confidence }) => {
        if (!bbox || bbox.length < 4) return;
        const [x1, y1, x2, y2] = bbox;
        const w = x2 - x1;
        const h = y2 - y1;
        const isFire = className === 'fire';
        const color = isFire ? '#f97316' : '#94a3b8'; // orange for fire, slate for smoke
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.strokeRect(x1, y1, w, h);

        const label = `${isFire ? '🔥' : '💨'} ${className} ${Math.round(confidence * 100)}%`;
        const textW = ctx.measureText(label).width + 10;
        ctx.fillStyle = isFire ? 'rgba(249,115,22,0.9)' : 'rgba(100,116,139,0.9)';
        ctx.fillRect(x1, y1 - 26, textW, 26);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x1 + 5, y1 - 7);
      });
    }
  }, [detections, bufW, bufH]);

  return (
    <canvas
      ref={canvasRef}
      width={bufW}
      height={bufH}
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
    />
  );
});

DetectionOverlay.displayName = 'DetectionOverlay';

/**
 * Loading/Connecting State
 */
const StreamLoading = ({ status, cameraName }) => {
  const messages = {
    connecting: 'Connecting to stream...',
    device_offline: 'Edge device offline',
    error: 'Stream error',
  };

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/80">
      {status === 'connecting' && (
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
      )}
      <span className="text-gray-300 text-sm">
        {messages[status] || 'Initializing...'}
      </span>
      {cameraName && (
        <span className="text-gray-500 text-xs mt-1">{cameraName}</span>
      )}
    </div>
  );
};

/**
 * Main VideoStreamPlayer Component
 */
const VideoStreamPlayer = ({
  cameraId,
  className,
  showStats = false,
  preferH264 = true,
  showOverlay = true,
  onStreamStart,
  onStreamStop,
  onError,
}) => {
  const containerRef = useRef(null);
  const h264PlayerRef = useRef(null);
  const [useH264, setUseH264] = useState(MSE_SUPPORTED && preferH264);
  const [currentCodec, setCurrentCodec] = useState(null);
  const [streamStats, setStreamStats] = useState(null);

  const {
    connected,
    activeStreams,
    subscribeCamera,
    unsubscribeCamera,
  } = useLiveStreamStore();

  const streamState = activeStreams[cameraId] || {};
  const { frame, status, source, codec, stats, cameraName, detections, fps = 5 } = streamState;

  // Delay detections to match the buffered video so boxes track walking people.
  // Image (JPEG/WebP) mode has no video buffer, so no delay is applied there.
  const syncedDetections = useDelayedValue(
    detections,
    useH264 && codec === 'h264' ? DETECTION_SYNC_DELAY_MS : 0
  );

  // Stable refs for callbacks to avoid re-subscribe on parent re-render
  const onStreamStartRef = useRef(onStreamStart);
  const onStreamStopRef = useRef(onStreamStop);
  useEffect(() => { onStreamStartRef.current = onStreamStart; }, [onStreamStart]);
  useEffect(() => { onStreamStopRef.current = onStreamStop; }, [onStreamStop]);

  // Subscribe to camera stream on mount
  useEffect(() => {
    subscribeCamera(cameraId, { preferH264: useH264 });
    onStreamStartRef.current?.();

    return () => {
      unsubscribeCamera(cameraId);
      onStreamStopRef.current?.();
    };
  }, [cameraId, useH264, subscribeCamera, unsubscribeCamera]);

  // Auto-reconnect logic with exponential backoff
  useEffect(() => {
    const reconnectTimeoutRef = { current: null };
    const reconnectAttemptsRef = { current: 0 };
    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY = 2000; // 2 seconds

    const attemptReconnect = () => {
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        console.warn(`[VideoStreamPlayer] Max reconnect attempts reached for camera ${cameraId}`);
        onError?.(new Error('Max reconnect attempts reached'));
        return;
      }

      const delay = BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current);
      console.log(`[VideoStreamPlayer] Reconnecting camera ${cameraId} in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectAttemptsRef.current += 1;
        console.log(`[VideoStreamPlayer] Attempting to reconnect camera ${cameraId}`);
        subscribeCamera(cameraId, { preferH264: useH264 });
      }, delay);
    };

    // Monitor connection status and stream errors
    if (!connected && status !== 'connecting') {
      console.log(`[VideoStreamPlayer] WebSocket disconnected for camera ${cameraId}, scheduling reconnect`);
      attemptReconnect();
    } else if (status === 'error') {
      console.log(`[VideoStreamPlayer] Stream error for camera ${cameraId}, scheduling reconnect`);
      attemptReconnect();
    } else if (status === 'streaming' || status === 'connecting') {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connected, status, cameraId, useH264, subscribeCamera, onError]);

  // Keep-alive ping mechanism to detect stale streams
  useEffect(() => {
    if (status !== 'streaming') return;

    const STALE_THRESHOLD = 10000; // 10 seconds without frames
    const checkInterval = setInterval(() => {
      const stream = activeStreams[cameraId];
      if (stream?.lastFrameTime) {
        const timeSinceLastFrame = Date.now() - stream.lastFrameTime;
        if (timeSinceLastFrame > STALE_THRESHOLD) {
          console.warn(`[VideoStreamPlayer] Stream stale for camera ${cameraId}, resubscribing`);
          unsubscribeCamera(cameraId);
          setTimeout(() => {
            subscribeCamera(cameraId, { preferH264: useH264 });
          }, 1000);
        }
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkInterval);
  }, [status, cameraId, activeStreams, useH264, subscribeCamera, unsubscribeCamera]);

  // Handle codec changes
  useEffect(() => {
    if (codec && codec !== currentCodec) {
      setCurrentCodec(codec);

      // If receiving H.264 but not using H.264 player, switch
      if (codec === 'h264' && !useH264 && MSE_SUPPORTED) {
        setUseH264(true);
      }
      // If receiving non-H.264 but using H.264 player, switch to image
      else if (codec !== 'h264' && useH264) {
        setUseH264(false);
      }
    }
  }, [codec, currentCodec, useH264]);

  // Update stats
  useEffect(() => {
    if (stats) {
      setStreamStats(stats);
    }
  }, [stats]);

  // Handle H.264 data feeding
  useEffect(() => {
    if (useH264 && codec === 'h264' && frame && h264PlayerRef.current?.feedH264Data) {
      // Extract base64 data from data URL if present
      const base64Data = frame.startsWith('data:')
        ? frame.split(',')[1]
        : frame;
      h264PlayerRef.current.feedH264Data(base64Data);
    }
  }, [useH264, codec, frame]);

  // Handle H.264 errors by falling back to image mode
  const handleH264Error = useCallback((error) => {
    console.warn('[VideoStreamPlayer] H.264 playback error, falling back to image mode:', error);
    setUseH264(false);
    onError?.(error);
  }, [onError]);

  const isStreaming = status === 'streaming' && frame;
  const isLoading = !isStreaming && status !== 'error';

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative bg-gray-900 overflow-hidden',
        className
      )}
    >
      {/* Video/Image Player */}
      {isStreaming && (
        <>
          {codec === 'h264' ? (
            // H.264 frames are raw NAL bytes — they must go to jmuxer, NEVER to
            // an <img>. Feeding h264 base64 to ImagePlayer made the browser GET
            // it as a URL (ERR_HTTP2_PROTOCOL_ERROR) and show black. If MSE/H264
            // is unavailable, show loading rather than mis-render the bytes.
            useH264 ? (
              <H264Player
                ref={h264PlayerRef}
                cameraId={cameraId}
                fps={fps}
                onError={handleH264Error}
              />
            ) : (
              <StreamLoading status="connecting" cameraName={cameraName} />
            )
          ) : (
            <ImagePlayer frame={frame} />
          )}
          {showOverlay && syncedDetections && <DetectionOverlay detections={syncedDetections} />}
        </>
      )}

      {/* Loading State */}
      {isLoading && (
        <StreamLoading status={status} cameraName={cameraName} />
      )}

      {/* Stats Overlay */}
      {showStats && isStreaming && (
        <StreamStats
          stats={streamStats}
          codec={currentCodec}
          source={source}
        />
      )}

      {/* Connection Status Indicator */}
      {!connected && (
        <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-red-900/80 backdrop-blur-sm rounded px-2 py-1">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-200 text-xs">Disconnected</span>
        </div>
      )}

      {/* Source Badge */}
      {source === 'edge' && isStreaming && (
        <div className="absolute top-2 left-2 bg-amber-900/80 backdrop-blur-sm rounded px-2 py-1">
          <span className="text-amber-200 text-xs font-medium">REMOTE</span>
        </div>
      )}
    </div>
  );
};

export default VideoStreamPlayer;

// Named exports for individual components
export { H264Player, ImagePlayer, StreamStats, StreamLoading, MSE_SUPPORTED };
